import { SupportedChainId } from 'constants/chains'
import { useTokensPriceUsdQuery } from 'graphql/__generated__/types-and-hooks'
import { clients } from 'graphql/apollo'
import { PollingInterval } from 'graphql/constants'
import { isAddress } from 'utils'
import { buildCurrencyKey } from 'utils/currencyKey'

export type ContractInput = {
  address?: string | undefined
  chain: SupportedChainId
}

function getNetworkAddresses(contracts: ContractInput[], network: SupportedChainId): string[] {
  const filtred = contracts.filter(({ chain }) => chain === network)
  return filtred.map(({ address }) => address?.toLocaleLowerCase() ?? '')
}

type PriceMap = { [key: string]: number | undefined }

export function useMultiChainTokensPriceUsd(contracts: ContractInput[]) {
  const zkAddresses = getNetworkAddresses(contracts, SupportedChainId.ZK_SYNC)
  const { data: zkData, loading: zkLoading } = useTokensPriceUsdQuery({
    variables: {
      tokenAddresses: zkAddresses,
    },
    pollInterval: PollingInterval.Fast,
    client: clients[SupportedChainId.ZK_SYNC],
  })

  const fantomAddresses = getNetworkAddresses(contracts, SupportedChainId.FANTOM)
  const { data: fantomData, loading: fantomLoading } = useTokensPriceUsdQuery({
    variables: {
      tokenAddresses: fantomAddresses,
    },
    pollInterval: PollingInterval.Fast,
    client: clients[SupportedChainId.FANTOM],
  })

  const arbAddresses = getNetworkAddresses(contracts, SupportedChainId.ARBITRUM)
  const { data: arbData, loading: arbLoading } = useTokensPriceUsdQuery({
    variables: {
      tokenAddresses: arbAddresses,
    },
    pollInterval: PollingInterval.Fast,
    client: clients[SupportedChainId.ARBITRUM],
  })

  const priceMap: PriceMap = {}

  if (zkData?.tokens && zkData?.bundle) {
    for (const token of zkData.tokens) {
      const address = isAddress(token.id)
      if (address) {
        priceMap[buildCurrencyKey(SupportedChainId.ZK_SYNC, address)] =
          parseFloat(token.derivedETH) * parseFloat(zkData.bundle?.ethPriceUSD)
      }
    }
  }

  if (fantomData?.tokens && fantomData?.bundle) {
    for (const token of fantomData.tokens) {
      const address = isAddress(token.id)
      if (address) {
        priceMap[buildCurrencyKey(SupportedChainId.FANTOM, address)] =
          parseFloat(token.derivedETH) * parseFloat(fantomData.bundle?.ethPriceUSD)
      }
    }
  }

  if (arbData?.tokens && arbData?.bundle) {
    for (const token of arbData.tokens) {
      const address = isAddress(token.id)
      if (address) {
        priceMap[buildCurrencyKey(SupportedChainId.ARBITRUM, address)] =
          parseFloat(token.derivedETH) * parseFloat(arbData.bundle?.ethPriceUSD)
      }
    }
  }

  return {
    priceMap,
    loading: zkLoading || fantomLoading || arbLoading,
  }
}
